<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

import { useProductGroup } from '../../composables/use-product-group'
import { useDebounce } from '../../composables/use-debounce'
import Product from '../shared/product/Product.vue'
import EditorialStarRating from '../shared/product/EditorialStarRating.vue'
import ProsAndCons from '../shared/product/ProsAndCons.vue'
import TechnicalData from '../shared/product/TechnicalData.vue'
import Anchor from '../shared/product/Anchor.vue'
import OtherPartners from '../shared/product/OtherPartners.vue'

import type { RawProduct } from '../../composables/use-product-group'
import type { ProductAutoComparisonParagraph } from '../../utils/paragraph-schema/paragraphs'
import type { Brand } from '@xcel-next/core/enums'

// defines number of product columns by viewport width within the visible viewport
const NUM_COLS_BY_VIEWPORT: Record<number, number> = {
  0: 1,
  360: 2,
  640: 3,
  1000: 2,
  1100: 3
}

const props = withDefaults(defineProps<{
  productAutoComparison?: ProductAutoComparisonParagraph['productAutoComparison'],
  brand?: Brand,
  isContentCommerce?: boolean,
  isPreview?: boolean
}>(), {
  isContentCommerce: false,
  isPreview: false
})

const showRanking = computed(() => props.productAutoComparison?.showRanking ?? false)
const overlineTestReport = computed(() => props.productAutoComparison?.overlineTestReport)
const products = computed(() => props.productAutoComparison?.products)

const { unifiedProducts, propertyExists, syncLabelHeights } = useProductGroup({
  brand: () => props.brand,
  // productType is a loose string in the composer schema (dynamic product types), cast to the composable's discriminated union
  products: () => products.value?.filter((product): product is NonNullable<typeof product> => !!product) as unknown as RawProduct[] | undefined,
  isPreview: () => props.isPreview
})

const rootDiv = ref<HTMLElement | null>(null)
const autocomparisonwrapper = ref<HTMLElement | null>(null)
const prevnextwrapper = ref<HTMLElement | null>(null)
const autocomparisonslider = ref<HTMLElement | null>(null)

const canScrollRight = ref(false)
const canScrollLeft = ref(false)
const scrollAmount = ref(0)
const scrolling = ref(false)
const scrollIntervals = ref<ReturnType<typeof setInterval>[]>([])
const itemWidth = ref(0)
const resizeObserver = ref<ResizeObserver | null>(null)

const noExtendedInformationBelowOverall = computed(() =>
  !unifiedProducts.value.some(product => product.extendedInformationOrder.includes('otherPartners'))
)

const calculateItemWidth = (wrapperWidth: number) => {
  const vpWidth = props.isPreview ? (document.getElementById('asset-preview')?.clientWidth ?? 0) : window.innerWidth
  let numCols = 1
  Object.keys(NUM_COLS_BY_VIEWPORT).forEach(key => {
    if (vpWidth >= Number(key)) {
      numCols = NUM_COLS_BY_VIEWPORT[Number(key)]
    }
  })
  itemWidth.value = Math.floor(wrapperWidth / numCols)
}

const setItemsWidth = (itemsList: HTMLElement[]) => {
  itemsList.forEach((item) => {
    item.style.width = `${itemWidth.value}px`
  })
}

const setWidths = () => {
  const wrapper = autocomparisonwrapper.value
  if (!wrapper) return
  const wrapperWidth = wrapper.clientWidth

  const productsList = [...wrapper.querySelectorAll<HTMLElement>('a.bx-product')]
  const otherPartners = [...wrapper.querySelectorAll<HTMLElement>('.bx-product-auto-comparison__other-partners > div')]
  const ratingsList = [...wrapper.querySelectorAll<HTMLElement>('div.bx-product__rating')]
  const prosAndConsList = [...wrapper.querySelectorAll<HTMLElement>('ul.bx-product__pros-and-cons')]
  const techDataList = [...wrapper.querySelectorAll<HTMLElement>('.bx-tech-data__table > div > div')]
  const anchorsList = [...wrapper.querySelectorAll<HTMLElement>('a.bx-product__anchor')]
  const allLists = [productsList, otherPartners, ratingsList, prosAndConsList, techDataList, anchorsList]

  calculateItemWidth(wrapperWidth)
  allLists.forEach(setItemsWidth)
}

const observeWrapperResize = () => {
  if (resizeObserver.value) return
  // observe the wrapper itself (not just window resize) so late CSS/fonts/images finishing layout also trigger a recalculation
  const wrapper = autocomparisonwrapper.value
  if (!wrapper) return
  resizeObserver.value = new ResizeObserver(() => {
    setWidths()
    checkIfCanScroll()
  })
  resizeObserver.value.observe(wrapper)
}

const checkIfCanScroll = () => {
  const wrapper = autocomparisonslider.value

  if (!scrolling.value && wrapper !== null) {
    canScrollRight.value = wrapper.scrollLeft < wrapper.scrollWidth - wrapper.clientWidth
    canScrollLeft.value = wrapper.scrollLeft > 0
  }
}

const scroll = (direction: 'left' | 'right') => {
  const wrapper = autocomparisonslider.value
  if (!wrapper) return
  scrolling.value = true

  let exactScrollLeft = wrapper.scrollLeft

  const slideTimer = setInterval(() => {
    let scrollStep = Math.floor(itemWidth.value / 10)

    if (scrollAmount.value + scrollStep > itemWidth.value) {
      scrollStep = itemWidth.value - scrollAmount.value
    }

    exactScrollLeft = direction === 'right' ? (exactScrollLeft + scrollStep) : (exactScrollLeft - scrollStep)
    scrollAmount.value += scrollStep
    wrapper.scrollLeft = Math.floor(exactScrollLeft)

    if (scrollAmount.value >= itemWidth.value) {
      clearInterval(slideTimer)
      scrollIntervals.value = scrollIntervals.value.filter(id => id !== slideTimer)
      scrolling.value = false
      scrollAmount.value = 0
      checkIfCanScroll()
    }
  }, 15)
  scrollIntervals.value.push(slideTimer)
}

const clearAllIntervals = () => {
  scrollIntervals.value.forEach(intervalId => clearInterval(intervalId))
  scrollIntervals.value = []
}

const handleResize = () => {
  const sliderWrapper = autocomparisonslider.value
  setWidths()
  if (sliderWrapper) sliderWrapper.scrollLeft = 0
  checkIfCanScroll()
  clearAllIntervals()
}

const handleSwipe = (event: WheelEvent) => {
  if (event.deltaX !== 0) {
    checkIfCanScroll()
  }
}

const handleScroll = () => {
  const wrapper = autocomparisonwrapper.value
  const prevNext = prevnextwrapper.value
  if (!wrapper || !prevNext) return
  const wrapperBottom = wrapper.getBoundingClientRect().bottom
  const prevNextBottom = prevNext.getBoundingClientRect().bottom

  if ((prevNextBottom + 100) > wrapperBottom) {
    prevNext.style.transition = 'opacity 0.05s ease-in-out'
    prevNext.style.opacity = '0'
  } else if ((prevNextBottom) <= wrapperBottom) {
    prevNext.style.transition = 'opacity 0.05s ease-in-out'
    prevNext.style.opacity = '1'
  }
}

watch(products, () => {
  if (props.isPreview) {
    nextTick(() => {
      if ((products.value?.length ?? 0) > 0) {
        setWidths()
        checkIfCanScroll()
        observeWrapperResize()
      }
    })
  }
})

const { debounce: debounceResize } = useDebounce()
const { debounce: debounceSwipe } = useDebounce()
const { debounce: debounceScroll } = useDebounce()

const onWindowResize = () => debounceResize(handleResize, 100)
const onWindowWheel = (event: WheelEvent) => debounceSwipe(() => handleSwipe(event), 100)
const onWindowScroll = () => debounceScroll(handleScroll, 100)

onMounted(() => {
  window.addEventListener('resize', onWindowResize)
  window.addEventListener('wheel', onWindowWheel, { passive: true })
  window.addEventListener('scroll', onWindowScroll, { passive: true })
  if ((products.value?.length ?? 0) > 0) {
    setWidths()
    checkIfCanScroll()
    observeWrapperResize()
  }

  // sync label heights after component is mounted
  nextTick(() => {
    syncLabelHeights(rootDiv.value)
  })
})

onBeforeUnmount(() => {
  clearAllIntervals()
  window.removeEventListener('resize', onWindowResize)
  window.removeEventListener('wheel', onWindowWheel)
  window.removeEventListener('scroll', onWindowScroll)
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})
</script>

<template>
  <div ref="rootDiv"
       class="bx-content bx-content--structure-full bx-product-group bx-product-auto-comparison bx-typo--product-group"
       data-tc="product-auto-comparison">
    <span class="bx-badge bx-badge--affiliates"
          data-tc="product-group-badge" />
    <div ref="autocomparisonwrapper"
         class="bx-product-auto-comparison__wrapper">
      <div ref="prevnextwrapper"
           class="bx-product-auto-comparison__prev-next">
        <i v-if="canScrollRight"
           class="icon icon-arrow_pager-next"
           @click="scroll('right')">
          <svg aria-label="nach rechts scrollen">
            <use href="#arrow_pager-next" />
          </svg>
        </i>
        <i v-if="canScrollLeft"
           class="icon icon-arrow_pager-prev"
           @click="scroll('left')">
          <svg aria-label="nach links scrollen">
            <use href="#arrow_pager-prev" />
          </svg>
        </i>
      </div>
      <div v-if="products?.length"
           ref="autocomparisonslider"
           class="bx-product-auto-comparison__slider">
        <div class="bx-product-auto-comparison__products">
          <Product v-for="(product, productIndex) in unifiedProducts"
                   :key="`product-${productIndex}`"
                   product-group-type="autocomparison"
                   :product="product"
                   :product-index="productIndex"
                   :show-ranking="showRanking"
                   :is-content-commerce="isContentCommerce"
                   :is-preview="isPreview"
                   :no-extended-information-below="noExtendedInformationBelowOverall"
                   :reserve-space-for-label="propertyExists('label')"
                   :reserve-space-for-label-shopping-days="propertyExists('shoppingDaysLabel')" />
        </div>
        <div v-if="propertyExists('otherPartners')"
             class="bx-product-auto-comparison__other-partners">
          <OtherPartners v-for="(product, productIndex) in unifiedProducts"
                         :key="`${productIndex}-partners`"
                         :unified-products="[product]" />
        </div>
        <div v-if="propertyExists('editorialStarRating')"
             class="bx-product-auto-comparison__editorial-star-ratings">
          <EditorialStarRating v-for="(product, productIndex) in unifiedProducts"
                               :key="`${productIndex}-rating`"
                               size="small"
                               title="Unsere Bewertung"
                               :unique-product-id="product.uniqueId"
                               :rating="(product.editorialStarRating as number | undefined)" />
        </div>
        <ProsAndCons v-if="propertyExists('prosAndCons')"
                     :is-in-autocomparison="true"
                     :display-max-items="3"
                     :unified-products="unifiedProducts" />
        <TechnicalData v-if="propertyExists('technicalData')"
                       :is-in-autocomparison="true"
                       :unified-products="unifiedProducts" />
        <Anchor :unified-products="unifiedProducts"
                :overline-test-report="overlineTestReport"
                :is-in-autocomparison="true" />
      </div>
    </div>
  </div>
</template>
